import { Link } from 'react-router-dom';
import type { components } from '@bookingdei/api-client/types';
import { useLanguage } from '../../shell/language-context';
import { useFavorites } from '../../shell/favorites-context';
import type { HomeFormat } from './use-home-format';

type BusinessSummary = components['schemas']['BusinessSummary'];

// The design's home listing card (Popular grid DC:586-642, carousels DC:694-716):
// 146 px photo, category eyebrow, name + Verified, area, rating and "From" price. A
// card surfaced from an ad slot carries the visible "Sponsored" label (AP-D10).
// Cards open the business page at /b/{slug} (DR-40 — there is no listing route).
//
// `photo="background"` paints the cover as a background (the Popular grid), `"img"`
// as an image element (the carousels) — the design uses one of each.
export function HomeListingCard({
  business,
  fmt,
  photo,
  heart = false,
  snap = false,
}: {
  business: BusinessSummary;
  fmt: HomeFormat;
  photo: 'background' | 'img';
  heart?: boolean;
  snap?: boolean;
}) {
  const { t, lang } = useLanguage();
  const favorites = useFavorites();
  const cover = business.cover_image?.['800'] ?? business.cover_image?.['400'];
  const name = lang === 'bn' && business.name_bn ? business.name_bn : business.name;
  const saved = favorites.isSaved(business.id);

  return (
    <div className={snap ? 'hm-card hm-card--snap' : 'hm-card'}>
      <Link to={`/b/${business.slug}`} className="hm-card__a">
        <div
          className="hm-card__media"
          style={photo === 'background' && cover ? { backgroundImage: `url('${cover}')` } : undefined}
        >
          {photo === 'img' && cover ? <img className="hm-card__img" src={cover} alt={name} loading="lazy" decoding="async" /> : null}
          {business.sponsored ? <span className="hm-card__sp">{t('dc50.card.sponsored')}</span> : null}
        </div>
        <div className="hm-card__body">
          <div className="hm-card__cat">{fmt.catLabel(business.category_id)}</div>
          <div className="hm-card__nameline">
            <span className="hm-card__name">{name}</span>
            {business.verified ? <span className="hm-card__ver">✓ {t('dc50.card.verified')}</span> : null}
          </div>
          <div className="hm-card__area">{business.area_name}</div>
          <div className="hm-card__foot">
            <span className="hm-card__rate">
              ★ {fmt.rating(business.rating)} <span className="hm-card__count">({fmt.num(business.review_count)})</span>
            </span>
            <span className="hm-card__price">{fmt.priceFrom(business)}</span>
          </div>
        </div>
      </Link>
      {heart ? (
        <button
          type="button"
          className={saved ? 'hm-card__heart is-on' : 'hm-card__heart'}
          aria-pressed={saved}
          aria-label={t(saved ? 'dc50.card.unsave' : 'dc50.card.save', { name })}
          onClick={() => favorites.toggle(business.id)}
        >
          {saved ? '♥' : '♡'}
        </button>
      ) : null}
    </div>
  );
}
